/**
 * Rogue Alloy van tools (content review 2026-09-26, section 10, P3).
 *
 * Four independent tools, each server-rendered by its own section so the
 * explanation, the formula and (where the section has known defaults) a
 * worked example are useful with no JavaScript at all:
 *   - sections/rogue-tyre-calculator.liquid
 *   - sections/rogue-load-check.liquid
 *   - sections/rogue-offset-explainer.liquid
 *   - sections/rogue-pcd-helper.liquid
 *
 * Each section's interactive controls sit inside a `[data-rogue-tool-inputs]`
 * wrapper rendered with the `hidden` attribute, so a shopper with no
 * JavaScript never sees empty or half-working form fields: this module is
 * the only thing that reveals them, per tool, once its own listeners are
 * safely attached.
 *
 * Pure calculation functions live at the top (unit tested by
 * tests/rogue-tools.test.js); the DOM wiring below only reads form values,
 * calls them, and writes the results back into the page. No third-party
 * libraries, no bundler: loaded directly as an ES module, exactly like
 * assets/rogue-vans.js and assets/rogue-pages.js.
 */

/* -------------------------------------------------------------------------
 * Small shared helpers
 * ---------------------------------------------------------------------- */

/**
 * Hides a tool's "turn on JavaScript" fallback line once its real controls
 * are safely wired up. The fallback stays in the DOM (not removed), so a
 * tool whose script fails partway through still leaves the page in a
 * sensible state rather than an empty gap.
 * @param {Element} root
 */
function hideNoscriptFallback(root) {
  const fallback = root.querySelector('[data-rogue-tool-noscript]');
  if (fallback) fallback.hidden = true;
}

/**
 * Formats a number to a fixed number of decimals as a plain string ("58.0",
 * "685.9"), the same shape `| round: 1` produces server-side in Liquid.
 * @param {number} value
 * @param {number} decimals
 * @returns {string}
 */
export function formatFixed(value, decimals) {
  return value.toFixed(decimals);
}

/**
 * Formats a whole number of kg with a thousands separator ("1,000"), the
 * way the load check's "at least [X] kg each" line reads.
 * @param {number} value
 * @returns {string}
 */
export function formatKg(value) {
  const rounded = Math.round(value);
  return rounded.toLocaleString('en-GB');
}

/* -------------------------------------------------------------------------
 * 1. Tyre size calculator
 * ---------------------------------------------------------------------- */

/**
 * Overall tyre diameter in mm: rim (inches) x 25.4, plus twice the sidewall
 * height (section width x profile / 100).
 * @param {number|string} rim
 * @param {number|string} width
 * @param {number|string} profile
 * @returns {number|null}
 */
export function computeTyreDiameterMm(rim, width, profile) {
  const rimNum = Number(rim);
  const widthNum = Number(width);
  const profileNum = Number(profile);
  if (![rimNum, widthNum, profileNum].every((n) => Number.isFinite(n) && n > 0)) return null;
  return rimNum * 25.4 + (2 * widthNum * profileNum) / 100;
}

/**
 * @typedef {object} RogueTyreComparison
 * @property {number} currentDiameter
 * @property {number} newDiameter
 * @property {number} diffMm
 * @property {number} diffPercent
 * @property {number} rideHeightChangeMm
 * @property {{reading: number, trueSpeed: number}[]} speedoReadings
 */

/**
 * @param {{currentWidth:*, currentProfile:*, currentRim:*, newWidth:*, newProfile:*, newRim:*}} sizes
 * @returns {RogueTyreComparison|null}
 */
export function computeTyreComparison(sizes) {
  const currentDiameter = computeTyreDiameterMm(sizes.currentRim, sizes.currentWidth, sizes.currentProfile);
  const newDiameter = computeTyreDiameterMm(sizes.newRim, sizes.newWidth, sizes.newProfile);
  if (currentDiameter == null || newDiameter == null) return null;

  const diffMm = newDiameter - currentDiameter;
  const diffPercent = (diffMm / currentDiameter) * 100;
  const rideHeightChangeMm = diffMm / 2;
  const speedoReadings = [30, 50, 70].map((reading) => ({
    reading,
    trueSpeed: (reading * newDiameter) / currentDiameter,
  }));

  return { currentDiameter, newDiameter, diffMm, diffPercent, rideHeightChangeMm, speedoReadings };
}

function initTyreCalculator() {
  const root = document.querySelector('[data-rogue-tool="tyre-calculator"]');
  if (!root) return;

  const inputs = root.querySelector('[data-rogue-tool-inputs]');
  const fields = Array.from(root.querySelectorAll('[data-tyre-field]'));
  if (!inputs || fields.length === 0) return;

  const out = {
    currentValue: root.querySelector('[data-tyre-current-value]'),
    newValue: root.querySelector('[data-tyre-new-value]'),
    diffMm: root.querySelector('[data-tyre-diff-mm]'),
    diffPercent: root.querySelector('[data-tyre-diff-percent]'),
    rideHeight: root.querySelector('[data-tyre-ride-height]'),
    speedo30: root.querySelector('[data-tyre-speedo-30]'),
    speedo50: root.querySelector('[data-tyre-speedo-50]'),
    speedo70: root.querySelector('[data-tyre-speedo-70]'),
  };

  function readSizes() {
    const value = (group, field) => {
      const el = fields.find(
        (f) => f.getAttribute('data-tyre-group') === group && f.getAttribute('data-tyre-field') === field
      );
      return el ? el.value : null;
    };
    return {
      currentWidth: value('current', 'width'),
      currentProfile: value('current', 'profile'),
      currentRim: value('current', 'rim'),
      newWidth: value('new', 'width'),
      newProfile: value('new', 'profile'),
      newRim: value('new', 'rim'),
    };
  }

  function render() {
    const result = computeTyreComparison(readSizes());
    if (!result) return;
    if (out.currentValue) out.currentValue.textContent = `${formatFixed(result.currentDiameter, 1)} mm`;
    if (out.newValue) out.newValue.textContent = `${formatFixed(result.newDiameter, 1)} mm`;
    if (out.diffMm) out.diffMm.textContent = `${formatFixed(result.diffMm, 1)} mm`;
    if (out.diffPercent) out.diffPercent.textContent = `${formatFixed(result.diffPercent, 1)}%`;
    if (out.rideHeight) out.rideHeight.textContent = `${formatFixed(result.rideHeightChangeMm, 1)} mm`;
    const speedoEls = [out.speedo30, out.speedo50, out.speedo70];
    result.speedoReadings.forEach((row, index) => {
      const el = speedoEls[index];
      if (el) el.textContent = formatFixed(row.trueSpeed, 1);
    });
  }

  fields.forEach((field) => field.addEventListener('change', render));
  hideNoscriptFallback(root);
  inputs.hidden = false;
  render();
}

/* -------------------------------------------------------------------------
 * 2. Load check
 * ---------------------------------------------------------------------- */

/** The section's own number inputs carry these as `min`/`max` (spec: 500 to
 * 4,000 kg), which HTML5 alone does not enforce: `type="number"` still
 * accepts an out-of-range value typed directly, and this form reads
 * `.value` on every keystroke rather than waiting on form validation
 * (content review 2026-09-26, P3: an axle maximum of 99,999 kg was reaching
 * `minRating` and rendering "at least 50,000 kg each"). */
export const LOAD_CHECK_MIN_KG = 500;
export const LOAD_CHECK_MAX_KG = 4000;

/**
 * @param {number|string} frontMax
 * @param {number|string} rearMax
 * @returns {{frontPerWheel:number, rearPerWheel:number, minRating:number}|{error:'range'}|null}
 */
export function computeLoadCheck(frontMax, rearMax) {
  const front = Number(frontMax);
  const rear = Number(rearMax);
  if (!Number.isFinite(front) || !Number.isFinite(rear) || front <= 0 || rear <= 0) return null;
  if (
    front < LOAD_CHECK_MIN_KG ||
    front > LOAD_CHECK_MAX_KG ||
    rear < LOAD_CHECK_MIN_KG ||
    rear > LOAD_CHECK_MAX_KG
  ) {
    return { error: 'range' };
  }
  const frontPerWheel = front / 2;
  const rearPerWheel = rear / 2;
  return { frontPerWheel, rearPerWheel, minRating: Math.max(frontPerWheel, rearPerWheel) };
}

function initLoadCheck() {
  const root = document.querySelector('[data-rogue-tool="load-check"]');
  if (!root) return;

  const inputs = root.querySelector('[data-rogue-tool-inputs]');
  const frontField = root.querySelector('[data-load-field="front"]');
  const rearField = root.querySelector('[data-load-field="rear"]');
  const placeholder = root.querySelector('[data-load-placeholder]');
  const rangeError = root.querySelector('[data-load-range-error]');
  const computed = root.querySelector('[data-load-computed]');
  if (!inputs || !frontField || !rearField || !placeholder || !computed) return;

  const out = {
    front: root.querySelector('[data-load-front-per-wheel]'),
    rear: root.querySelector('[data-load-rear-per-wheel]'),
    min: root.querySelector('[data-load-min-rating]'),
  };

  function render() {
    const result = computeLoadCheck(frontField.value, rearField.value);
    if (!result) {
      placeholder.hidden = false;
      if (rangeError) rangeError.hidden = true;
      computed.hidden = true;
      return;
    }
    if (result.error === 'range') {
      placeholder.hidden = true;
      if (rangeError) rangeError.hidden = false;
      computed.hidden = true;
      return;
    }
    if (out.front) out.front.textContent = formatKg(result.frontPerWheel);
    if (out.rear) out.rear.textContent = formatKg(result.rearPerWheel);
    if (out.min) out.min.textContent = formatKg(result.minRating);
    placeholder.hidden = true;
    if (rangeError) rangeError.hidden = true;
    computed.hidden = false;
  }

  frontField.addEventListener('input', render);
  rearField.addEventListener('input', render);
  hideNoscriptFallback(root);
  inputs.hidden = false;
  render();
}

/* -------------------------------------------------------------------------
 * 3. Offset explainer
 * ---------------------------------------------------------------------- */

/**
 * @param {number|string} etMm
 * @param {number|string} widthJ
 * @returns {{outer:number, inner:number}|null}
 */
export function computeWheelFaces(etMm, widthJ) {
  const et = Number(etMm);
  const j = Number(widthJ);
  if (!Number.isFinite(et) || !Number.isFinite(j)) return null;
  const half = (j * 25.4) / 2;
  return { outer: half - et, inner: half + et };
}

/**
 * @param {{currentEt:*, currentWidthJ:*, newEt:*, newWidthJ:*}} values
 */
export function computeOffsetComparison(values) {
  const current = computeWheelFaces(values.currentEt, values.currentWidthJ);
  const next = computeWheelFaces(values.newEt, values.newWidthJ);
  if (!current || !next) return null;
  return {
    current,
    new: next,
    deltaOuter: next.outer - current.outer,
    deltaInner: next.inner - current.inner,
  };
}

/**
 * @param {number} deltaOuter
 * @returns {string}
 */
export function formatOuterSentence(deltaOuter) {
  const abs = formatFixed(Math.abs(deltaOuter), 1);
  if (deltaOuter < 0) return `The outside face sits ${abs} mm further in.`;
  return `The outside face sits ${abs} mm further out, towards the arch.`;
}

/**
 * @param {number} deltaInner
 * @returns {string}
 */
export function formatInnerSentence(deltaInner) {
  const abs = formatFixed(Math.abs(deltaInner), 1);
  if (deltaInner < 0) return `The inside face sits ${abs} mm further from the brakes and suspension.`;
  return `The inside face sits ${abs} mm closer to the brakes and suspension.`;
}

const OFFSET_SVG_WIDTH = 640;
const OFFSET_SVG_HEIGHT = 260;
const OFFSET_HUB_X = 220;
const OFFSET_PX_PER_MM = 1.6;
const OFFSET_RECT_HEIGHT = 96;

/**
 * Pixel geometry for the two rectangles in the 640x260 cross-section, given
 * both wheels' outer/inner faces in mm. The hub face is a fixed vertical
 * line; each rectangle spans from its outer face (towards the arch, left)
 * to its inner face (towards the brakes and suspension, right), clamped to
 * stay inside the canvas even for an extreme ET/width combination.
 * @param {{outerOld:number, innerOld:number, outerNew:number, innerNew:number}} faces
 */
export function computeOffsetDiagram(faces) {
  const clamp = (v) => Math.max(4, Math.min(OFFSET_SVG_WIDTH - 4, v));
  const rectFor = (outerMm, innerMm) => {
    const left = clamp(OFFSET_HUB_X - outerMm * OFFSET_PX_PER_MM);
    const right = clamp(OFFSET_HUB_X + innerMm * OFFSET_PX_PER_MM);
    return { x: Math.min(left, right), width: Math.abs(right - left) };
  };
  return {
    width: OFFSET_SVG_WIDTH,
    height: OFFSET_SVG_HEIGHT,
    hubX: OFFSET_HUB_X,
    rectY: (OFFSET_SVG_HEIGHT - OFFSET_RECT_HEIGHT) / 2,
    rectHeight: OFFSET_RECT_HEIGHT,
    current: rectFor(faces.outerOld, faces.innerOld),
    new: rectFor(faces.outerNew, faces.innerNew),
  };
}

function initOffsetExplainer() {
  const root = document.querySelector('[data-rogue-tool="offset-explainer"]');
  if (!root) return;

  const inputs = root.querySelector('[data-rogue-tool-inputs]');
  const currentEt = root.querySelector('[data-offset-field="current-et"]');
  const currentWidth = root.querySelector('[data-offset-field="current-width"]');
  const newEt = root.querySelector('[data-offset-field="new-et"]');
  const newEtRange = root.querySelector('[data-offset-field="new-et-range"]');
  const newWidth = root.querySelector('[data-offset-field="new-width"]');
  if (!inputs || !currentEt || !currentWidth || !newEt || !newWidth) return;

  const outerLine = root.querySelector('[data-offset-outer-line]');
  const innerLine = root.querySelector('[data-offset-inner-line]');
  const svg = root.querySelector('[data-offset-svg]');
  const rectCurrent = root.querySelector('[data-offset-rect-current]');
  const rectNew = root.querySelector('[data-offset-rect-new]');

  function render() {
    const comparison = computeOffsetComparison({
      currentEt: currentEt.value,
      currentWidthJ: currentWidth.value,
      newEt: newEt.value,
      newWidthJ: newWidth.value,
    });
    if (!comparison) return;

    const outerSentence = formatOuterSentence(comparison.deltaOuter);
    const innerSentence = formatInnerSentence(comparison.deltaInner);
    if (outerLine) outerLine.textContent = outerSentence;
    if (innerLine) innerLine.textContent = innerSentence;

    const diagram = computeOffsetDiagram({
      outerOld: comparison.current.outer,
      innerOld: comparison.current.inner,
      outerNew: comparison.new.outer,
      innerNew: comparison.new.inner,
    });
    if (rectCurrent) {
      rectCurrent.setAttribute('x', String(diagram.current.x));
      rectCurrent.setAttribute('width', String(diagram.current.width));
    }
    if (rectNew) {
      rectNew.setAttribute('x', String(diagram.new.x));
      rectNew.setAttribute('width', String(diagram.new.width));
    }
    if (svg) svg.setAttribute('aria-label', `${outerSentence} ${innerSentence}`);
  }

  function syncRangeFromNumber() {
    if (newEtRange) newEtRange.value = newEt.value;
    render();
  }

  function syncNumberFromRange() {
    newEt.value = newEtRange.value;
    render();
  }

  currentEt.addEventListener('input', render);
  currentWidth.addEventListener('change', render);
  newEt.addEventListener('input', syncRangeFromNumber);
  newWidth.addEventListener('change', render);
  if (newEtRange) newEtRange.addEventListener('input', syncNumberFromRange);

  hideNoscriptFallback(root);
  inputs.hidden = false;
  render();
}

/* -------------------------------------------------------------------------
 * 4. PCD helper
 * ---------------------------------------------------------------------- */

/** Centre-to-centre-to-PCD multiplier for two neighbouring studs, by stud count. */
export const PCD_NEIGHBOUR_MULTIPLIER = { 4: 1.4142, 5: 1.7013, 6: 2.0 };

/** The stud patterns Rogue makes wheels for (design spec, P3). */
export const PCD_PATTERNS = [
  { studs: 5, pcd: 108, label: '5x108' },
  { studs: 5, pcd: 112, label: '5x112' },
  { studs: 5, pcd: 114.3, label: '5x114.3' },
  { studs: 5, pcd: 118, label: '5x118' },
  { studs: 5, pcd: 120, label: '5x120' },
  { studs: 5, pcd: 130, label: '5x130' },
  { studs: 5, pcd: 160, label: '5x160' },
  { studs: 6, pcd: 120, label: '6x120' },
  { studs: 6, pcd: 125, label: '6x125' },
  { studs: 6, pcd: 130, label: '6x130' },
  { studs: 6, pcd: 139.7, label: '6x139.7' },
  { studs: 6, pcd: 180, label: '6x180' },
  { studs: 6, pcd: 205, label: '6x205' },
];

/**
 * How far a reading can sit from a pattern and still be that pattern, in mm
 * (3 Nov 2026). Rogue's method, from the edge of one stud to the centre of
 * the stud opposite, reads about one stud radius (6 to 7 mm on these vans)
 * long or short of the true PCD, depending on which edge the tape starts
 * at; 8 mm covers that plus a millimetre of tape. On 5 studs the outer edge
 * lands within about 2 mm, but the inner edge reads 11 to 15 mm short, so the
 * same window is kept there too. Every pattern inside it is shown, and more
 * than one means "send us a photo" rather than a confident answer.
 */
export const PCD_MATCH_WINDOW_MM = 8;

/**
 * @param {number|string} studs
 * @param {'across'|'neighbouring'} method
 * @param {number|string} measurementMm
 * @returns {number|null}
 */
export function computePcdFromMeasurement(studs, method, measurementMm) {
  const value = Number(measurementMm);
  if (!Number.isFinite(value) || value <= 0) return null;
  if (method === 'across') return value;
  const multiplier = PCD_NEIGHBOUR_MULTIPLIER[Number(studs)];
  if (!multiplier) return null;
  return value * multiplier;
}

/**
 * Every pattern for that stud count within PCD_MATCH_WINDOW_MM of the
 * reading, smallest PCD first.
 * @param {number|string} studs
 * @param {number} pcd
 * @returns {Array<{studs:number, pcd:number, label:string, distance:number}>}
 */
export function findPcdCandidates(studs, pcd) {
  const studsNum = Number(studs);
  const value = Number(pcd);
  if (!Number.isFinite(value)) return [];
  return PCD_PATTERNS
    .filter((p) => p.studs === studsNum && Math.abs(value - p.pcd) <= PCD_MATCH_WINDOW_MM)
    .map((p) => ({ ...p, distance: Math.abs(value - p.pcd) }));
}

/**
 * The closest pattern inside the window, or null.
 * @param {number|string} studs
 * @param {number} pcd
 * @returns {{studs:number, pcd:number, label:string, distance:number}|null}
 */
export function findNearestPcdPattern(studs, pcd) {
  return findPcdCandidates(studs, pcd).reduce((best, candidate) => (!best || candidate.distance < best.distance ? candidate : best), null);
}

/**
 * What the PCD helper shows for a reading: nothing yet, one pattern, several
 * (too close to tell apart by tape: send a photo), or none of Rogue's.
 * @param {number|string} studs
 * @param {'across'|'neighbouring'} method
 * @param {number|string} measurementMm
 * @returns {{kind:'placeholder'|'one'|'several'|'none', pcd:number|null, patterns:Array<{studs:number, pcd:number, label:string, distance:number}>}}
 */
export function pcdHelperResult(studs, method, measurementMm) {
  const pcd = computePcdFromMeasurement(studs, method, measurementMm);
  if (pcd == null) return { kind: 'placeholder', pcd: null, patterns: [] };
  const patterns = findPcdCandidates(studs, pcd);
  return { kind: patterns.length === 0 ? 'none' : patterns.length === 1 ? 'one' : 'several', pcd, patterns };
}

/**
 * "6x120, 6x125 or 6x130".
 * @param {string[]} labels
 * @returns {string}
 */
export function joinWithOr(labels) {
  const list = (Array.isArray(labels) ? labels : []).filter(Boolean);
  return list.length > 1 ? `${list.slice(0, -1).join(', ')} or ${list[list.length - 1]}` : list[0] || '';
}

/**
 * @param {{studs:number, pcd:number}} pattern
 * @returns {string}
 */
export function collectionSlugForPattern(pattern) {
  return `wheels-${pattern.studs}x${Math.floor(pattern.pcd)}`;
}

function initPcdHelper() {
  const root = document.querySelector('[data-rogue-tool="pcd-helper"]');
  if (!root) return;

  const inputs = root.querySelector('[data-rogue-tool-inputs]');
  const studsField = root.querySelector('[data-pcd-field="studs"]');
  // 3 Nov 2026: the guide teaches Rogue's one method (edge of one stud to the
  // centre of the stud opposite, or furthest on 5 studs), read as the PCD
  // itself, so the section has no method field any more; one still on a page
  // keeps working.
  const methodField = root.querySelector('[data-pcd-field="method"]');
  const measurementField = root.querySelector('[data-pcd-field="measurement"]');
  const placeholder = root.querySelector('[data-pcd-placeholder]');
  const match = root.querySelector('[data-pcd-match]');
  const several = root.querySelector('[data-pcd-several]');
  const noMatch = root.querySelector('[data-pcd-no-match]');
  if (!inputs || !studsField || !measurementField || !placeholder || !match || !noMatch) return;

  const out = {
    label: root.querySelector('[data-pcd-label]'),
    value: root.querySelector('[data-pcd-value]'),
    link: root.querySelector('[data-pcd-link]'),
    severalLabels: root.querySelector('[data-pcd-several-labels]'),
    severalValue: root.querySelector('[data-pcd-several-value]'),
  };

  function showOnly(el) {
    [placeholder, match, several, noMatch].forEach((candidate) => {
      if (candidate) candidate.hidden = candidate !== el;
    });
  }

  function render() {
    const result = pcdHelperResult(studsField.value, methodField ? methodField.value : 'across', measurementField.value);
    if (result.kind === 'placeholder') {
      showOnly(placeholder);
      return;
    }
    if (result.kind === 'none' || (result.kind === 'several' && !several)) {
      showOnly(noMatch);
      return;
    }
    if (result.kind === 'several') {
      // More than one pattern fits the reading: list them all and ask for a photo.
      if (out.severalLabels) out.severalLabels.textContent = joinWithOr(result.patterns.map((p) => p.label));
      if (out.severalValue) out.severalValue.textContent = formatFixed(result.pcd, 1);
      showOnly(several);
      return;
    }
    const found = result.patterns[0];
    if (out.label) out.label.textContent = found.label;
    if (out.value) out.value.textContent = formatFixed(result.pcd, 1);
    if (out.link) out.link.setAttribute('href', `/collections/${collectionSlugForPattern(found)}`);
    showOnly(match);
  }

  studsField.addEventListener('change', render);
  methodField?.addEventListener('change', render);
  measurementField.addEventListener('input', render);
  hideNoscriptFallback(root);
  inputs.hidden = false;
  render();
}

/* -------------------------------------------------------------------------
 * Init
 * ---------------------------------------------------------------------- */

function initAll() {
  initTyreCalculator();
  initLoadCheck();
  initOffsetExplainer();
  initPcdHelper();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
}
